import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { getRoleLevelContent, getRoleLevelsByRole } from '../../config/pages/gdad/contentBlocks';
import roleSelectionDataJson from '../../config/pages/gdad/data/role-selection.json';
import { validateField } from '../../utils/form-utils';
import { isUnsupportedGdadRole } from '../../utils/gdad-role-policy';
import { shouldResetGdadRoleSelectionOnGradeChange } from './gdadRoleSelectionLoad';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
import GdadRoleLevelDefinition from './GdadRoleLevelDefinition';

type RoleFamily = {
  name: string;
  roles: string[];
};

type RoleSelectionData = {
  role_families: RoleFamily[];
  role_levels_by_role: Record<string, string[]>;
};

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

const roleSelectionData = roleSelectionDataJson as RoleSelectionData;

const toNonEmptyString = (value: unknown): string => (typeof value === 'string' ? value : '');
const isNotInUseRoleLevel = (value: string): boolean => value.trim().toUpperCase() === 'NOT IN USE';

const hasSelectableRoleLevels = (role: string): boolean => {
  const levels = getRoleLevelsByRole(role) || [];
  return levels.some((level) => !isNotInUseRoleLevel(level));
};

export default function GdadRoleSelectionInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, watch, getFieldState, getValues, setValue, clearErrors } = useFormContext();
  const baseFieldName = String(question.name);
  const gradeFieldName = question.lookupQuestion || 'grade';
  const familyFieldName = `${baseFieldName}-family`;
  const roleFieldName = baseFieldName;
  const roleLevelFieldName = `${baseFieldName}-level`;
  const baseId = anchorId || baseFieldName;

  const selectedGrade = toNonEmptyString(watch(gradeFieldName));
  const selectedRoleFamily = toNonEmptyString(watch(familyFieldName));
  const selectedRole = toNonEmptyString(watch(roleFieldName));
  const selectedRoleLevel = toNonEmptyString(watch(roleLevelFieldName));

  // Hide excluded roles for new selections and hide any roles/families that only have
  // role levels marked as "NOT IN USE". If an older form already saved one of the excluded
  // roles, keep that exact saved role visible so opening the form does not wipe the answer.
  const rolesByFamily: Record<string, string[]> = {};
  for (const family of roleSelectionData.role_families) {
    const roles = Array.isArray(family.roles) ? family.roles : [];
    const selectableRoles = roles.filter(
      (role) => !isUnsupportedGdadRole(role) && hasSelectableRoleLevels(role),
    );
    const isSavedRoleInFamily =
      selectedRole !== '' && roles.includes(selectedRole) && hasSelectableRoleLevels(selectedRole);

    rolesByFamily[family.name] = isSavedRoleInFamily && !selectableRoles.includes(selectedRole)
      ? [...selectableRoles, selectedRole]
      : selectableRoles;
  }

  const roleFamilyNames = roleSelectionData.role_families
    .map((family) => family.name)
    .filter((familyName) => (rolesByFamily[familyName] || []).length > 0);

  const availableRoles = selectedRoleFamily ? rolesByFamily[selectedRoleFamily] || [] : [];
  const roleLevels = selectedRole ? getRoleLevelsByRole(selectedRole) || [] : [];

  // The framework lists role levels from junior to most senior; reverse for seniority-first options.
  const availableRoleLevels = selectedRole
    ? roleLevels
        .filter((roleLevel) => !isNotInUseRoleLevel(roleLevel))
        .slice()
        .reverse()
    : [];
  const selectedRoleLevelContent = getRoleLevelContent(selectedRole, selectedRoleLevel);

  const roleLevelSelectRef = useRef<HTMLSelectElement | null>(null);
  const shouldScrollToRoleLevelRef = useRef(false);
  useEffect(() => {
    if (!shouldScrollToRoleLevelRef.current) return;
    if (!selectedRoleLevel) return;

    shouldScrollToRoleLevelRef.current = false;
    roleLevelSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedRoleLevel]);

  const resetFields = (fieldNames: string[]) => {
    for (const fieldName of fieldNames) {
      if (toNonEmptyString(getValues(fieldName)) !== '') {
        setValue(fieldName, '', { shouldDirty: true, shouldValidate: false });
      }
    }
    clearErrors(fieldNames);
  };

  const previousGradeRef = useRef<string | null>(null);
  useEffect(() => {
    if (previousGradeRef.current === null) {
      previousGradeRef.current = selectedGrade;
      return;
    }

    if (
      shouldResetGdadRoleSelectionOnGradeChange(
        previousGradeRef.current,
        selectedGrade,
      )
    ) {
      resetFields([familyFieldName, roleFieldName, roleLevelFieldName]);
    }
    previousGradeRef.current = selectedGrade;
  }, [
    clearErrors,
    familyFieldName,
    getValues,
    roleFieldName,
    roleLevelFieldName,
    selectedGrade,
    setValue,
  ]);

  useEffect(() => {
    // Clear selections that are genuinely invalid after filtering, while allowing older saved
    // excluded roles to remain visible on existing forms.
    if (selectedRoleFamily && !roleFamilyNames.includes(selectedRoleFamily)) {
      resetFields([familyFieldName, roleFieldName, roleLevelFieldName]);
      return;
    }

    if (selectedRole && selectedRoleFamily && !availableRoles.includes(selectedRole)) {
      resetFields([roleFieldName, roleLevelFieldName]);
    }
  }, [
    availableRoles,
    familyFieldName,
    resetFields,
    roleFamilyNames,
    roleFieldName,
    roleLevelFieldName,
    selectedRole,
    selectedRoleFamily,
  ]);

  useEffect(() => {
    if (!selectedRoleLevel) return;
    if (availableRoleLevels.includes(selectedRoleLevel)) return;
    setValue(roleLevelFieldName, '', { shouldDirty: true, shouldValidate: false });
    clearErrors([roleLevelFieldName]);
  }, [availableRoleLevels, clearErrors, roleLevelFieldName, selectedRoleLevel, setValue]);

  const isRequired = question.rules?.required === true;

  const familyRegister = register(familyFieldName, {
    validate: (value: string) => {
      if (!isRequired) return true;
      return validateField('Role family', { required: true }, value, question.errorMessages) || true;
    },
  });
  const roleRegister = register(roleFieldName, {
    validate: (value: string) => {
      if (!isRequired || selectedRoleFamily === '') return true;
      return validateField('Role', { required: true }, value, question.errorMessages) || true;
    },
  });
  const roleLevelRegister = register(roleLevelFieldName, {
    validate: (value: string) => {
      if (!isRequired || selectedRole === '') return true;
      return validateField('Role level', { required: true }, value, question.errorMessages) || true;
    },
  });
  const { ref: roleLevelRegisterRef, ...roleLevelRegisterRest } = roleLevelRegister;
  const familyError = getFieldState(familyFieldName).error;
  const roleError = getFieldState(roleFieldName).error;
  const roleLevelError = getFieldState(roleLevelFieldName).error;
  return (
    <>
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className={`govuk-form-group ${familyError ? 'govuk-form-group--error' : ''}`}>
        <label className="govuk-label" htmlFor={`${baseId}-family`}>
          Role family
        </label>
        {!selectedGrade && <p className="govuk-hint">Select grade first</p>}
        {familyError && <ErrorMessage error={familyError} />}
        <select
          {...familyRegister}
          id={`${baseId}-family`}
          className={`govuk-select ${familyError ? 'govuk-select--error' : ''}`}
          disabled={disabled || selectedGrade === ''}
          onChange={(event) => {
            familyRegister.onChange(event);
            resetFields([roleFieldName, roleLevelFieldName]);
          }}
        >
          <option value="">Select an option</option>
          {roleFamilyNames.map((roleFamily) => (
            <option value={roleFamily} key={roleFamily}>
              {roleFamily}
            </option>
          ))}
        </select>
      </div>

      <div className={`govuk-form-group ${roleError ? 'govuk-form-group--error' : ''}`}>
        <label className="govuk-label" htmlFor={`${baseId}-role`}>
          Role
        </label>
        {roleError && <ErrorMessage error={roleError} />}
        <select
          {...roleRegister}
          id={`${baseId}-role`}
          className={`govuk-select ${roleError ? 'govuk-select--error' : ''}`}
          disabled={disabled || selectedRoleFamily === ''}
          onChange={(event) => {
            roleRegister.onChange(event);
            resetFields([roleLevelFieldName]);
          }}
        >
          <option value="">Select an option</option>
          {availableRoles.map((role) => (
            <option value={role} key={role}>
              {role}
            </option>
          ))}
        </select>
      </div>

      <div className={`govuk-form-group ${roleLevelError ? 'govuk-form-group--error' : ''}`}>
        <label className="govuk-label" htmlFor={`${baseId}-role-level`}>
          Role level
        </label>
        {roleLevelError && <ErrorMessage error={roleLevelError} />}
        <select
          {...roleLevelRegisterRest}
          id={`${baseId}-role-level`}
          className={`govuk-select ${roleLevelError ? 'govuk-select--error' : ''}`}
          disabled={disabled || selectedRole === ''}
          ref={(element) => {
            roleLevelRegisterRef(element);
            roleLevelSelectRef.current = element;
          }}
          onChange={(event) => {
            if (event.target.value) {
              // When a user re-selects role level, scroll so the role level select is at the top of the viewport.
              shouldScrollToRoleLevelRef.current = true;
            }
            roleLevelRegister.onChange(event);
            resetFields([]);
          }}
        >
          <option value="">Select an option</option>
          {availableRoleLevels.map((roleLevel) => (
            <option value={roleLevel} key={roleLevel}>
              {roleLevel}
            </option>
          ))}
        </select>
      </div>
      {selectedRoleLevelContent && (
        <>
          <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
          <h2 className="govuk-heading-m">{selectedRoleLevel}</h2>
          <GdadRoleLevelDefinition
            intro={selectedRoleLevelContent.intro}
            bullets={selectedRoleLevelContent.bullets}
            grades={selectedRoleLevelContent.grades}
          />
        </>
      )}
    </>
  );
}
